import React, { useEffect, useRef, useState } from "react";
import { useAppInfo } from "../../../helpers/hooks/common-hook";
import { fileUpload, getConfig, logoUploadUrlGet, saveConfig } from "../../../services/config";
import TextBox from "../_components/textbox";
import UploadAvatar from "../_components/upload-avatar";
import { Spinner } from 'flowbite-react';
import Message from "../../../components/toast-message";

import { defaultSettings, widgetLayoutId, widgetLayouts } from "./web-chat-settings";

function SettingsSection({ title, description, children }) {
  return <section className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
    <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
    <p className="mb-5 mt-1 text-xs leading-5 text-gray-500">{description}</p>
    <div className="space-y-5">{children}</div>
  </section>;
}

// A small drawing of where each launcher sits on a page.
function LayoutThumbnail({ layout }) {
  const bar = layout.launcherStyle === "text-bar";
  const centered = layout.position === "bottom-center";
  return (
    <span aria-hidden="true" className="relative block h-14 w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
      <span className="absolute left-2 top-2 h-1.5 w-10 rounded-full bg-gray-200" />
      <span className="absolute left-2 top-5 h-1.5 w-14 rounded-full bg-gray-200" />
      {bar
        ? <span className={`absolute bottom-2 h-3 w-12 rounded-full border border-gray-300 bg-white shadow-sm ${centered ? "left-1/2 -translate-x-1/2" : "right-2"}`} />
        : <span className="absolute bottom-2 right-2 h-4 w-4 rounded-full bg-primary" />}
    </span>
  );
}

// Suggestions are stored as a list; blank lines are dropped when saving.
const cleanTips = tips => (Array.isArray(tips) ? tips : []).map(tip => String(tip).trim()).filter(Boolean);

const WebChatAppeareance = ({ onSettingsChange }) => {
  const { appId } = useAppInfo();
  const [chatBotSetting, setChatBotSetting] = useState(defaultSettings);
  const [savedSettings, setSavedSettings] = useState(null);
  const [formState, setFormState] = useState({ processing: false, success: false, failed: false, message: "" });
  const [dataLoadingState, setDataLoading] = useState({ processing: true, failed: false });
  const [logoUrlGenState, setLogoUrlGenState] = useState({ processing: false, errorMessage: "" });
  const uploadInProgress = useRef(false);
  const editedFields = useRef(new Set());

  useEffect(() => {
    let active = true;
    setChatBotSetting(defaultSettings);
    setSavedSettings(null);
    editedFields.current.clear();
    setDataLoading({ processing: true, failed: false });
    getConfig(`app_${appId}`, "web_integration", { throwOnError: true }).then(resp => {
      if (!active) return;
      const loaded = { ...defaultSettings, ...(resp?.data || {}) };
      setChatBotSetting(loaded);
      setSavedSettings(loaded);
      setDataLoading({ processing: false, failed: false });
    }).catch(() => {
      if (active) setDataLoading({ processing: false, failed: true });
    });
    return () => { active = false; };
  }, [appId]);

  useEffect(() => {
    onSettingsChange?.(chatBotSetting);
  }, [chatBotSetting, onSettingsChange]);

  const handleSelectFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || uploadInProgress.current || formState.processing) return;
    uploadInProgress.current = true;
    setLogoUrlGenState({ processing: true, errorMessage: "" });
    setFormState({ processing: false, success: false, failed: false, message: "" });
    try {
      const fileExt = file.name.split(".").pop().toLowerCase();
      const { response, status } = await logoUploadUrlGet(appId, { fileExt });
      if (status !== 200 || !response?.uploadUrl || !response?.logoUrl) throw new Error("Unable to prepare logo upload");
      // Use this response's URL directly; React state still contains the previous URL.
      const uploaded = await fileUpload(response.uploadUrl, file);
      if (!uploaded?.ok) throw new Error("Unable to upload logo");
      // Only the permanent URL belongs in settings. Local previews must never be saved.
      // Uploads replace the same object, so invalidate cached copies of the old logo.
      const separator = response.logoUrl.includes("?") ? "&" : "?";
      const logo = `${response.logoUrl}${separator}v=${Date.now()}`;
      editedFields.current.add("logo");
      setChatBotSetting(current => ({ ...current, logo }));
      setLogoUrlGenState({ processing: false, errorMessage: "" });
    } catch {
      setLogoUrlGenState({ processing: false, errorMessage: "Logo upload failed. Please choose the image again." });
    } finally {
      uploadInProgress.current = false;
    }
  };

  const handleSaveSettings = async () => {
    if (formState.processing || uploadInProgress.current) return;
    setFormState({ processing: true, success: false, failed: false, message: "" });
    try {
      // Recover the existing config before saving after a failed initial read.
      // Apply only draft edits, preserving fields we could not load earlier.
      let settingsToSave = chatBotSetting;
      if (dataLoadingState.failed) {
        const resp = await getConfig(`app_${appId}`, "web_integration", { throwOnError: true });
        const edits = Object.fromEntries(Object.entries(chatBotSetting).filter(([key]) => editedFields.current.has(key)));
        settingsToSave = { ...defaultSettings, ...(resp?.data || {}), ...edits };
      }
      settingsToSave = { ...settingsToSave, tips: cleanTips(settingsToSave.tips) };
      const { status } = await saveConfig(appId, settingsToSave);
      if (status !== 200 && status !== 201) throw new Error("Save failed");
      setChatBotSetting(settingsToSave);
      setSavedSettings(settingsToSave);
      editedFields.current.clear();
      setDataLoading({ processing: false, failed: false });
      setFormState({ processing: false, success: true, failed: false, message: "Successfully saved settings" });
    } catch {
      setFormState({ processing: false, success: false, failed: true, message: "Unable to save settings. Please try again." });
    }
  };

  const handleChange = event => {
    const { name, value, type, checked } = event.target;
    editedFields.current.add(name);
    setChatBotSetting(current => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setFormState(current => ({ ...current, success: false }));
  };

  const handleChangeComplete = (field, color) => {
    editedFields.current.add(field);
    setChatBotSetting(current => ({ ...current, [field]: color }));
    setFormState(current => ({ ...current, success: false }));
  };

  const handleLayoutChange = layout => {
    editedFields.current.add("launcherStyle");
    editedFields.current.add("position");
    setChatBotSetting(current => ({ ...current, launcherStyle: layout.launcherStyle, position: layout.position }));
    setFormState(current => ({ ...current, success: false }));
  };

  const handleTipsChange = event => {
    editedFields.current.add("tips");
    const tips = event.target.value.split("\n");
    setChatBotSetting(current => ({ ...current, tips }));
    setFormState(current => ({ ...current, success: false }));
  };

  const selectedLayout = widgetLayoutId(chatBotSetting);
  const enableUpload = !logoUrlGenState.processing && !formState.processing;

  const dirty = JSON.stringify(savedSettings || defaultSettings) !== JSON.stringify(chatBotSetting);
  const textField = (name, label, placeholder) => (
    <div key={name}>
      <label htmlFor={name} className="mb-2 block text-xs font-medium text-gray-600">{label}</label>
      <TextBox id={name} name={name} type="text" value={chatBotSetting[name] || ""}
        placeholder={placeholder} onChange={handleChange} />
    </div>
  );

  if (dataLoadingState.processing) return <div className="flex justify-center py-16"><Spinner aria-label="Loading settings" /></div>;

  return (
    <div className="space-y-5">
      {formState.success && !dirty && <Message text="Your settings have been successfully saved" type="success" />}
      {dataLoadingState.failed && <p role="alert" className="rounded-xl bg-red-50 p-4 text-sm text-red-600">Saved settings could not be loaded. You can edit and preview changes; saving will retry the connection.</p>}
      {formState.failed && <p role="alert" className="text-sm text-red-600">{formState.message}</p>}
      <fieldset disabled={formState.processing} className="min-w-0 space-y-5">
        <SettingsSection title="Identity" description="Introduce your assistant with a name and a familiar face.">
          <div className="flex items-center gap-4">
            <div className="w-24 shrink-0"><UploadAvatar formState={formState} accept="image/*" disabled={!enableUpload}
              aria-label="Upload logo" enableUpload={enableUpload} onChange={handleSelectFile} value={chatBotSetting.logo} /></div>
            <div><p className="text-sm font-medium text-gray-700">Assistant logo</p>
              <p className="mt-1 text-xs text-gray-500">Choose an image for your widget.</p></div>
          </div>
          {logoUrlGenState.processing && <p role="status" className="text-xs text-gray-500">Uploading logo…</p>}
          {logoUrlGenState.errorMessage && <p role="alert" className="text-sm text-red-600">{logoUrlGenState.errorMessage}</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            {textField("title", "Name", "Your assistant")}
            {textField("subTitle", "Subtitle", "Here to help")}
          </div>
        </SettingsSection>
        <SettingsSection title="Messages" description="Set the tone for every new conversation.">
          <div>
            <label htmlFor="welcomeMessage" className="mb-2 block text-xs font-medium text-gray-600">Welcome message</label>
            <textarea id="welcomeMessage" name="welcomeMessage" rows={3} value={chatBotSetting.welcomeMessage || ""}
              placeholder="Hi there. How can I help you?" onChange={handleChange}
              className="block w-full resize-y rounded-xl border border-gray-200 bg-gray-50/80 px-3.5 py-3 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10" />
          </div>
          {textField("inputPlaceHolder", "Input placeholder", "Type a message...")}
        </SettingsSection>
        <SettingsSection title="Appearance" description="Match the widget to your brand's colors.">
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["headerBackgroundColor", "Brand color"], ["headerTextColor", "Header text color"],
              ["chatWindowBackgroundColor", "Chat background"], ["sendBarBackgroundColor", "Input background"],
            ].map(([field, label]) => (
              <label key={field} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
                <input type="color" aria-label={label} value={chatBotSetting[field] || defaultSettings[field]}
                  onChange={event => handleChangeComplete(field, event.target.value)}
                  className="h-9 w-9 shrink-0 cursor-pointer rounded-md border-0 bg-transparent p-0" />
                <span className="min-w-0"><span className="block text-xs font-medium text-gray-700">{label}</span>
                  <span className="mt-1 block font-mono text-xs uppercase text-gray-400">{chatBotSetting[field]}</span></span>
              </label>
            ))}
          </div>
        </SettingsSection>
        {chatBotSetting.launcherStyle === "text-bar" && (
          <SettingsSection title="Text bar glow" description="Add a soft, animated glow around the text bar.">
            <label className="flex cursor-pointer items-center justify-between gap-4 text-sm text-gray-700">
              <span>Enable glow effect</span>
              <input type="checkbox" name="textBarGlowEnabled" checked={chatBotSetting.textBarGlowEnabled !== false}
                onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
            </label>
            <label className={`flex items-center gap-3 rounded-xl border border-gray-200 p-3 ${chatBotSetting.textBarGlowEnabled === false ? "opacity-50" : ""}`}>
              <input type="color" aria-label="Glow color" value={chatBotSetting.textBarGlowColor || defaultSettings.textBarGlowColor}
                disabled={chatBotSetting.textBarGlowEnabled === false}
                onChange={event => handleChangeComplete("textBarGlowColor", event.target.value)}
                className="h-9 w-9 shrink-0 cursor-pointer rounded-md border-0 bg-transparent p-0 disabled:cursor-not-allowed" />
              <span><span className="block text-xs font-medium text-gray-700">Glow color</span>
                <span className="mt-1 block font-mono text-xs uppercase text-gray-400">{chatBotSetting.textBarGlowColor || defaultSettings.textBarGlowColor}</span></span>
            </label>
          </SettingsSection>
        )}
        <SettingsSection title="Placement & branding" description="Choose where visitors can find your assistant.">
          <fieldset>
            <legend className="mb-2 block text-xs font-medium text-gray-600">Widget style</legend>
            <div className="grid gap-3 sm:grid-cols-3">
              {widgetLayouts.map(layout => {
                const selected = selectedLayout === layout.id;
                return (
                  <label key={layout.id} className={`relative flex cursor-pointer flex-col gap-3 rounded-xl border p-3 transition focus-within:ring-2 focus-within:ring-primary/30 ${selected
                    ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"}`}>
                    <input type="radio" name="widgetLayout" value={layout.id} checked={selected}
                      onChange={() => handleLayoutChange(layout)} className="sr-only" />
                    <LayoutThumbnail layout={layout} />
                    <span>
                      <span className="block text-sm font-medium text-gray-800">{layout.label}</span>
                      <span className="mt-1 block text-xs leading-5 text-gray-500">{layout.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
          {chatBotSetting.launcherStyle === "text-bar" && (
            <div>
              <label htmlFor="tips" className="mb-2 block text-xs font-medium text-gray-600">Suggested questions</label>
              <textarea id="tips" name="tips" rows={3} value={(Array.isArray(chatBotSetting.tips) ? chatBotSetting.tips : []).join("\n")}
                placeholder={"What can you help me with?\nHow does pricing work?"} onChange={handleTipsChange}
                className="block w-full resize-y rounded-xl border border-gray-200 bg-gray-50/80 px-3.5 py-3 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10" />
              <p className="mt-2 text-xs text-gray-500">One per line. Up to three appear above the bar when visitors hover or click it.</p>
            </div>
          )}
          <label className="flex cursor-pointer items-center justify-between gap-4 border-t border-gray-100 pt-5 text-sm text-gray-700">
            <span>Show “Powered by BotCircuits”</span>
            <input type="checkbox" name="showPoweredBy" checked={chatBotSetting.showPoweredBy !== false}
              onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
          </label>
        </SettingsSection>
      </fieldset>
      <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
        <p role="status" className="text-xs text-gray-500">{formState.processing ? "Saving changes…" : dirty ? "Unsaved changes" : dataLoadingState.failed ? "Settings unavailable" : "Settings up to date"}</p>
        <button type="button" onClick={handleSaveSettings} disabled={!enableUpload}
          className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default WebChatAppeareance;
